import type { OrganizationApiKeyDto } from '@sara/api-client';
import { Button } from '@sara/ui/components/button';
import { DataPanel } from '@sara/ui/components/data-panel';

interface ApiKeyListProps {
  apiKeys: OrganizationApiKeyDto[];
  onRevoke: (apiKey: OrganizationApiKeyDto) => void;
  revokingId: string | null;
}

function formatDate(value: string | Date | null | undefined) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const COLUMNS = 'grid-cols-[1.4fr_1fr_1fr_7rem]';
const COLUMN_TEMPLATE = '1.4fr 1fr 1fr 7rem';

export function ApiKeyList({ apiKeys, onRevoke, revokingId }: ApiKeyListProps) {
  if (apiKeys.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 bg-card/40 px-6 py-20 text-center">
        <p className="font-display text-xl font-medium text-foreground">No API keys yet.</p>
        <p className="mt-2 max-w-xs text-sm text-muted-foreground">
          Create your first key to integrate with the AI Interviewer API.
        </p>
      </div>
    );
  }

  // Keep active keys at the top, revoked after.
  const sorted = [...apiKeys].sort((a, b) => {
    const aRevoked = a.revokedAt ? 1 : 0;
    const bRevoked = b.revokedAt ? 1 : 0;
    if (aRevoked !== bRevoked) return aRevoked - bRevoked;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <DataPanel>
      <DataPanel.Header columns={COLUMN_TEMPLATE}>
        <span>Name</span>
        <span>Last used</span>
        <span>Status</span>
        <span className="sr-only">Actions</span>
      </DataPanel.Header>
      <DataPanel.Body>
        <ul className="divide-y divide-border/60">
          {sorted.map((apiKey) => {
            const isRevoked = apiKey.revokedAt !== null;
            const lastUsed = formatDate(apiKey.lastUsedAt);
            const created = formatDate(apiKey.createdAt);

            return (
              <li key={apiKey.id} className={`grid ${COLUMNS} items-center gap-6 px-6 py-5`}>
                <div className="min-w-0">
                  <p
                    className={`truncate text-[15px] font-medium tracking-tight ${
                      isRevoked ? 'text-muted-foreground' : 'text-foreground'
                    }`}
                  >
                    {apiKey.label}
                  </p>
                  <p className="mt-1 text-xs tabular-nums text-muted-foreground">
                    Created {created}
                    {isRevoked && (
                      <>
                        {' · '}
                        Revoked {formatDate(apiKey.revokedAt)}
                      </>
                    )}
                  </p>
                </div>
                <span className="text-[13px] tabular-nums text-muted-foreground">
                  {lastUsed ?? 'Never'}
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 text-[12px] font-medium tracking-tight ${
                    isRevoked ? 'text-muted-foreground/80' : 'text-primary'
                  }`}
                >
                  <span
                    aria-hidden
                    className={`h-1 w-1 rounded-full ${
                      isRevoked ? 'bg-muted-foreground/50' : 'bg-primary'
                    }`}
                  />
                  {isRevoked ? 'Revoked' : 'Active'}
                </span>
                <div className="shrink-0 justify-self-end">
                  {!isRevoked && (
                    <Button
                      variant="outline"
                      shape="pill"
                      size="sm"
                      onClick={() => onRevoke(apiKey)}
                      disabled={revokingId === apiKey.id}
                    >
                      {revokingId === apiKey.id ? 'Revoking…' : 'Revoke'}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </DataPanel.Body>
      <DataPanel.Footer>
        <span className="tabular-nums">
          {apiKeys.length} {apiKeys.length === 1 ? 'key' : 'keys'}
        </span>
      </DataPanel.Footer>
    </DataPanel>
  );
}
